import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { http } from '../../../lib/api';
import { AppLayout } from '../../../components/layout';
import { Button, Card, Badge, SpinnerBlock, Select } from '../../../components/core';
import { NAV } from '../nav';
import { subscribeRealtimeEvents } from '../../../lib/ws';
import type { ConversationDto, ConversationStatus } from '@livestreamx/shared-types';

interface ConversationsResponse {
  items: ConversationDto[];
  total: number;
  page: number;
  pageSize: number;
}

export default function TenantChatList() {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState<ConversationDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [statusFilter, setStatusFilter] = useState<ConversationStatus | 'ALL'>('ALL');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [newSubject, setNewSubject] = useState('');
  const [newMessage, setNewMessage] = useState('');

  const loadConversations = async () => {
    setLoading(true);
    try {
      const query = new URLSearchParams();
      query.set('page', String(page));
      query.set('pageSize', String(pageSize));
      if (statusFilter !== 'ALL') query.set('status', statusFilter);
      const res = await http.get<ConversationsResponse>(`/api/chat/tenant/conversations?${query}`);
      setConversations(res.items);
      setTotal(res.total);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConversations();
    const unsubscribe = subscribeRealtimeEvents((event: unknown) => {
      const e = event as { type: string; data: ConversationDto };
      if (e.type === 'chat.conversation.created' || e.type === 'chat.conversation.updated') {
        setConversations((prev) => {
          const idx = prev.findIndex((c) => c.id === e.data.id);
          if (idx >= 0) {
            const next = [...prev];
            next[idx] = e.data;
            return next.sort((a, b) => new Date(b.lastMessageAt ?? '').getTime() - new Date(a.lastMessageAt ?? '').getTime());
          }
          return [e.data, ...prev].sort((a, b) => new Date(b.lastMessageAt ?? '').getTime() - new Date(a.lastMessageAt ?? '').getTime());
        });
      }
    });
    return unsubscribe;
  }, [page, statusFilter]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setCreateError('');
    try {
      await http.post('/api/chat/tenant/conversations', { subject: newSubject, initialMessage: newMessage });
      setShowCreateModal(false);
      setNewSubject('');
      setNewMessage('');
      loadConversations();
    } catch (err: unknown) {
      setCreateError((err as Error).message ?? 'Failed to create conversation');
    } finally {
      setCreating(false);
    }
  };

  const hasMore = () => page * pageSize < total;

  return (
    <AppLayout brand="LiveStreamX" navGroups={NAV}>
      <div className="page-header">
        <div>
          <h1 className="page-title">Support Chat</h1>
          <p className="page-subtitle">Communicate with platform administrators</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
          New Conversation
        </Button>
      </div>

      <Card>
        <div className="card-header">
          <Select
            value={statusFilter}
            onChange={(v) => setStatusFilter(v as ConversationStatus | 'ALL')}
            options={[
              { value: 'ALL', label: 'All' },
              { value: 'OPEN', label: 'Open' },
              { value: 'CLOSED', label: 'Closed' },
              { value: 'ARCHIVED', label: 'Archived' },
            ]}
          />
        </div>
        <div className="space-y-3 p-4">
          {conversations.map((c) => (
            <a key={c.id} href={`/chat/${c.id}`} className="block">
              <Card className="hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{c.subject ?? 'General Support'}</span>
                        <Badge tone={c.status === 'OPEN' ? 'active' : c.status === 'CLOSED' ? 'warning' : 'archived'}>{c.status}</Badge>
                        {c.unreadTenant > 0 && <Badge tone="error">{c.unreadTenant} unread</Badge>}
                      </div>
                      <div className="text-sm text-muted truncate">{c.lastMessagePreview ?? 'No messages yet'}</div>
                    </div>
                  </div>
                  <div className="text-sm text-muted whitespace-nowrap ml-4">
                    {c.lastMessageAt ? new Date(c.lastMessageAt).toLocaleString() : '—'}
                  </div>
                </div>
              </Card>
            </a>
          ))}</div>
        {conversations.length === 0 && !loading && (
          <div className="text-center py-12 text-muted">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="mx-auto mb-4 opacity-50"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>
            <p className="text-lg font-medium">No conversations yet</p>
            <p className="mt-1">Start a conversation to get help from our support team</p>
          </div>
        )}
        {conversations.length === 0 && loading && <SpinnerBlock />}
        <div className="px-4 py-2 border-t">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted">Showing {conversations.length} of {total}</span>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>Prev</Button>
              <Button variant="ghost" size="sm" onClick={() => setPage(p => p + 1)} disabled={!hasMore()}>Next</Button>
            </div>
          </div>
        </div>
      </Card>

      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div style={{ backgroundColor: 'var(--surface)', borderRadius: 'var(--radius-lg)', padding: '1.5rem', width: '100%', maxWidth: '28rem', margin: '1rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem', color: 'var(--text-strong)' }}>Start New Conversation</h2>
            <form onSubmit={handleCreate}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '0.35rem' }}>Subject</label>
                <input
                  type="text"
                  value={newSubject}
                  onChange={(v) => setNewSubject(v.target.value)}
                  placeholder="Optional subject line"
                  style={{ width: '100%', padding: '0.55rem 0.75rem', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-strong)', backgroundColor: 'var(--surface)', color: 'var(--text)', fontSize: '0.92rem', fontFamily: 'inherit', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '0.35rem' }}>Message *</label>
                <textarea
                  value={newMessage}
                  onChange={(v) => setNewMessage(v.target.value)}
                  placeholder="Describe your issue or question…"
                  rows={4}
                  style={{ width: '100%', padding: '0.55rem 0.75rem', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-strong)', backgroundColor: 'var(--surface)', color: 'var(--text)', fontSize: '0.92rem', fontFamily: 'inherit', resize: 'vertical', minHeight: '90px', boxSizing: 'border-box' }}
                />
              </div>
              {createError && <div style={{ color: 'var(--danger)', fontSize: '0.85rem', marginBottom: '0.5rem' }}>{createError}</div>}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                <Button type="button" variant="secondary" onClick={() => setShowCreateModal(false)}>Cancel</Button>
                <Button disabled={creating || !newMessage.trim()} type="submit">
                  {creating ? 'Creating…' : 'Start Conversation'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AppLayout>
  );
}